import {
  Button,
  Flex,
  Modal,
  SwitchField,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { businessPaths } from '@jane/business-admin/util';
import { ConfirmChangeModal } from '@jane/shared/components';

import styles from './ModalPageWrapper.module.css';

/**
 * Recursively checks if there are any actual error messages in the errors object.
 * React-hook-form can have nested error structures like {"subItem": {}} even when
 * there are no actual errors, so we need to check for actual error messages.
 */
const hasErrors = (errors: FieldErrors<FieldValues>): boolean => {
  for (const key in errors) {
    const error = errors[key];
    if (error) {
      if ('message' in error && error.message) {
        return true;
      }
      if (typeof error === 'object' && !('message' in error)) {
        if (hasErrors(error as FieldErrors<FieldValues>)) {
          return true;
        }
      }
    }
  }
  return false;
};

type PreviewButtonProps = {
  disable?: boolean;
  label?: string;
  loading?: boolean;
  onClick: () => void;
};

export interface ModalPageWrapperProps<T extends FieldValues> {
  children: ReactNode;
  formMethods: UseFormReturn<T>;
  isLoading?: boolean;
  onSubmit: SubmitHandler<T>;
  preview?: PreviewButtonProps;
  secondaryAction?: ReactNode;
  submitLabel?: string;
  title: string;
  toggle?: {
    control: Control<T>;
    label: string;
    name: Path<T>;
  };
}

const useForceFallback = () => {
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(businessPaths.premium(), { replace: true });
  }, [navigate]);
};

export const ModalPageWrapper = <T extends FieldValues>({
  children,
  formMethods,
  isLoading,
  onSubmit,
  preview,
  secondaryAction,
  submitLabel,
  title,
  toggle,
}: ModalPageWrapperProps<T>) => {
  const goBack = useForceFallback();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const {
    formState: { isDirty, isValid, errors },
  } = formMethods;

  const onRequestClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      goBack();
    }
  }, [isDirty, goBack]);

  const publishable = useMemo(() => {
    return isDirty && isValid && !isLoading && !hasErrors(errors);
  }, [isDirty, isValid, isLoading, errors]);

  return (
    <>
      <Modal
        appId="parent"
        open
        variant="full-screen"
        onRequestClose={onRequestClose}
        overflow="auto"
      >
        <Form.BaseForm
          formMethods={formMethods}
          name={`${title.toLowerCase().split(' ').join('-')}-form`}
          onSubmit={onSubmit}
        >
          <Modal.Header
            title={title}
            actions={
              <Flex ml="auto" flexDirection="row">
                {toggle && (
                  <Controller
                    control={toggle.control}
                    name={toggle.name}
                    render={({ field }) => (
                      <SwitchField
                        label={toggle.label || 'Visible'}
                        name="toggle"
                        checked={field.value}
                        onChange={field.onChange}
                        className={styles['switchField']}
                      />
                    )}
                  />
                )}

                {secondaryAction}

                {preview ? (
                  <Button
                    ml="auto"
                    mr={16}
                    label={preview.label || 'Preview'}
                    type="button"
                    variant="secondary"
                    onClick={preview.onClick}
                    disabled={preview.disable || hasErrors(errors)}
                    loading={preview.loading}
                  />
                ) : null}

                <Form.SubmitButton
                  ml="auto"
                  label={submitLabel ?? 'Publish'}
                  variant="primary"
                  disabled={!publishable}
                  loading={isLoading}
                />
              </Flex>
            }
          />
          {children}
        </Form.BaseForm>
      </Modal>

      <ConfirmChangeModal
        open={showDiscardModal}
        title={
          <Typography variant="header-bold" mb={8} as="div">
            Discard changes?
          </Typography>
        }
        onConfirm={() => goBack()}
        onCancel={() => setShowDiscardModal(false)}
        confirmButtonVariant="destructive"
        confirmLabel="Discard"
        subtitle="You have made changes without publishing. If you continue, you will lose your progress."
      />
    </>
  );
};
